import {
  ArrowLeftOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  LinkOutlined,
  StarFilled,
  StarOutlined
} from "@ant-design/icons"
import {
  Button,
  Flex,
  Progress,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message
} from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { getProgressColor } from "../../Projects/constants/project-progress.ts"
import {
  type ToggleFavoriteResponse,
  toggleFavorite
} from "../api/global-map.api.ts"
import { parseFeatureId } from "../helpers/feature-id"
import { globalMapStatusStyle } from "../helpers/status-style"
import { EntityGlyphTile } from "../map/EntityGlyphTile.tsx"
import { GLOBAL_MAP_SURFACE_CLASS } from "../global-map-surface.ts"
import { selectQuickView } from "../redux/global-map.slice"
import { fetchQuickView } from "../redux/global-map.thunks"
import {
  type EntityQuickView as EntityQuickViewData,
  type EntityType,
  type LeadQuickView,
  type ProjectQuickView
} from "../redux/global-map.types"
import { emitMapEvent } from "../telemetry"
import { NearbyBreakdownSummary } from "./NearbyBreakdownSummary.tsx"
import { QuickViewFiles } from "./QuickViewFiles.tsx"
import { QuickViewNearby } from "./QuickViewNearby.tsx"
import {
  locationBadge,
  toFieldRows,
  toLeadFieldRows
} from "./quick-view-fields"
import { useNearbyFeatures } from "./useNearbyFeatures.ts"

const { Title, Text } = Typography

// Long enough for a screen reader to announce the unavailable message before
// the panel disappears out from under it.
const UNAVAILABLE_AUTO_CLOSE_MS = 2500

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// One config per entity, keyed by `quickView.entity_type`, so the Open action
// (label + href) is the only thing that differs between a project and a lead
// — everything else (focus-trap, Escape/Tab, auto-close-on-error, View on
// Map, Copy Link) is shared verbatim.
const ENTITY_CONFIG: Record<
  EntityType,
  { openLabel: string; openUrl: (qv: EntityQuickViewData) => string }
> = {
  project: {
    openLabel: "globalMap.quickView.actions.openProject",
    openUrl: (qv) => (qv as ProjectQuickView).actions.open_project_url
  },
  lead: {
    openLabel: "globalMap.quickView.actions.openLead",
    openUrl: (qv) => (qv as LeadQuickView).actions.open_lead_url
  }
}

interface EntityQuickViewProps {
  projectId: string
  onClose: () => void
  onOpenProject: (id: string) => void
  onRecenter: (id: string) => void
  // The Nearby tab's own row click — `QuickViewNearby` hands over the
  // clicked record's coordinate directly (see `MapTab.tsx`'s own
  // `handleSelectNearbyFeature` doc comment for why that record's
  // coordinate can't reliably be re-derived from `data.features`).
  onSelectNearbyFeature: (feature: {
    id: string
    entityType: EntityType
    latitude: number
    longitude: number
  }) => void
  // Which tab this record should open on — `"overview"` for every normal
  // selection (a marker, a search result, a Nearby row clicked forward),
  // `"nearby"` only when `MapTab.tsx`'s own Back stack just popped back to
  // this record, so the user lands exactly where they left off browsing
  // rather than back at Overview.
  initialTab: "overview" | "nearby"
  // Whether `MapTab.tsx`'s own Back stack has a previous record to return
  // to — built entirely from Nearby-row clicks (see `MapTab.tsx`'s
  // `handleSelectNearbyFeature`/`handleBackFromNearby`), so Back is only
  // ever offered while browsing a chain reached that way.
  canGoBack: boolean
  onBack: () => void
  originRef: React.RefObject<HTMLElement>
}

/**
 * The accessibility-critical equivalent of clicking a map marker: a `role`
 * `"dialog"` panel with its own focus trap, restoring focus to whatever
 * triggered it (a marker or a `ResultsList` row) on close. No antd `Modal` —
 * this repo has no existing focus-trap wrapper to match (grepped for
 * `focus-trap`/`FocusLock`), and `Modal`'s own focus return targets whatever
 * had focus when it opened rather than the caller-supplied `originRef`, which
 * the spec requires explicitly.
 *
 * Entity-agnostic: renders either a `ProjectQuickView` or `LeadQuickView`
 * payload (Task 13's `EntityQuickView` union), branching only on
 * `quickView.entity_type` for the Open action and the field rows.
 */
const EntityQuickView = ({
  projectId,
  onClose,
  onOpenProject,
  onRecenter,
  onSelectNearbyFeature,
  initialTab,
  canGoBack,
  onBack,
  originRef
}: EntityQuickViewProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const quickView = useAppSelector(selectQuickView)
  const panelRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const [activeTab, setActiveTab] = useState<string>(initialTab)

  // `projectId` is the selection's own id — the same namespaced `p:{uuid}`/
  // `l:{uuid}` feature id the map/results-list use to match markers and rows
  // — but the detail endpoints expect the bare UUID plus the entity type.
  useEffect(() => {
    const { id, entityType } = parseFeatureId(projectId)
    // Every selection reopens on `initialTab` — Overview for a normal
    // selection, or Nearby specifically when this render is a Back
    // navigation (`MapTab.tsx` sets both `projectId` and `initialTab` in the
    // same handler, so they always land together here). Carrying forward
    // whatever tab the PREVIOUS record happened to be on otherwise risks
    // landing on Files for a record whose own files haven't loaded yet.
    setActiveTab(initialTab)
    void dispatch(fetchQuickView({ id, entityType })).then((action) => {
      if (fetchQuickView.fulfilled.match(action)) {
        emitMapEvent("details_opened", {})
      }
    })
  }, [dispatch, projectId, initialTab])

  const handleClose = useCallback(() => {
    onClose()
    originRef.current?.focus()
  }, [onClose, originRef])

  // Reuses the same `?feature=` deep-link contract `useGlobalMapUrlSync`
  // already reads on mount, so the copied URL reopens this exact record's
  // quick view for whoever pastes it.
  const handleCopyLink = useCallback(() => {
    const url = `${window.location.origin}${window.location.pathname}?feature=${projectId}`
    void navigator.clipboard.writeText(url).then(() => {
      void message.success(t("globalMap.quickView.actions.linkCopied"))
    })
  }, [projectId, t])

  // A record that has been deleted/unlinked since the map fetched it —
  // rather than leave a dead panel open, announce it plainly and close.
  useEffect(() => {
    if (quickView.status !== "error") return
    const timer = setTimeout(handleClose, UNAVAILABLE_AUTO_CLOSE_MS)
    return () => clearTimeout(timer)
  }, [quickView.status, handleClose])

  useEffect(() => {
    closeButtonRef.current?.focus()
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation()
        handleClose()
        return
      }

      if (event.key !== "Tab" || !panelRef.current) return

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleClose])

  const data = quickView.data
  const isLoading = quickView.status === "loading" && !data
  const isError = quickView.status === "error"

  // `null` means "no optimistic override — trust `data.is_favorite`". Local
  // rather than written back into `quickView.data` (Redux) because this
  // component remounts fresh on every selection (`key={selectionSeq}` in
  // `MapTab.tsx`), so there's no staleness risk to guard against and no
  // reason to route a UI-only toggle through the store.
  const [favoriteOverride, setFavoriteOverride] = useState<boolean | null>(null)
  const isFavorite = favoriteOverride ?? data?.is_favorite ?? false

  const handleToggleFavorite = useCallback(() => {
    if (!data) return
    const optimistic = !isFavorite
    setFavoriteOverride(optimistic)
    void toggleFavorite(data.entity_type, data.id).then((response) => {
      // Every write response (POST included) is wrapped as
      // `{ status, details: { data } }` by the API's own renderer — only
      // GETs come back bare. `unwrapEnvelope` handles both shapes, so this
      // reads correctly whichever one actually arrives.
      const payload = response.ok
        ? unwrapEnvelope<ToggleFavoriteResponse>(response.data)
        : null
      if (payload) {
        setFavoriteOverride(payload.is_favorite)
      } else {
        setFavoriteOverride(!optimistic)
        void message.error(t("globalMap.quickView.actions.favoriteError"))
      }
    })
  }, [data, isFavorite, t])

  // One fetch behind BOTH the always-visible Nearby breakdown (rendered
  // under Overview and Files below) and the dedicated Nearby tab
  // (`QuickViewNearby`, which adds its own Lead/Project filter and row
  // list on top of the same `rows`/`counts`) — called once here so neither
  // consumer re-fetches independently.
  const nearby = useNearbyFeatures(data)

  const dialogLabel = data?.title ?? projectId
  const badge = data ? locationBadge(data, t) : { text: "", approximate: false }
  const fieldRows = data
    ? data.entity_type === "lead"
      ? toLeadFieldRows(data, t)
      : toFieldRows(data, t)
    : []
  const config = data ? ENTITY_CONFIG[data.entity_type] : null

  // Overview, Nearby and Files are real tabs. Activity and Team are both
  // removed entirely per explicit product direction (not just disabled —
  // this panel has no activity feed or team-list payload to grow into, and
  // the client asked for both gone rather than parked). Nearby reuses "Look
  // Around a Zip Code"'s own resolved-point radius filter, anchored at this
  // record's own coordinate (`QuickViewNearby`). Files reuses this app's own
  // project-documents/lead-attachments data (`QuickViewFiles`) — neither
  // needed new Global-Map-specific storage to enable.
  const tabItems = [
    { key: "overview", label: t("globalMap.quickView.tabs.overview") },
    { key: "nearby", label: t("globalMap.quickView.tabs.nearby") },
    { key: "files", label: t("globalMap.quickView.tabs.files") }
  ]

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={dialogLabel}
      aria-modal="true"
      className={`flex h-full w-full flex-col overflow-hidden ${GLOBAL_MAP_SURFACE_CLASS}`}
    >
      {/* The identity block (avatar/title/address/action icons), status
          pill and tabs are a fixed header — `shrink-0`, never inside the
          scrolling region below — so they stay visible ("the record summary
          should always be seen") whichever tab (Overview/Nearby/Files) is
          open and however long that tab's own content runs. Only the
          tab-specific body scrolls; the Open Project/View on Map footer is
          likewise pinned at the bottom, never pushed off-screen by a long
          Nearby or Files list. */}
      <div className="flex shrink-0 flex-col gap-3 p-4 pb-0">
        {/* Only ever rendered while browsing a chain of Nearby-row clicks
            (`MapTab.tsx`'s own back stack) — a normal single selection (a
            marker, a search result, opening the very first record) never has
            anything to go back to. */}
        {canGoBack && (
          <Button
            type="text"
            size="small"
            // antd's built-in `ArrowLeftOutlined` renders its own
            // `aria-label="arrow-left"` with no `aria-hidden` — left alone,
            // that merges into the button's computed accessible name
            // alongside the visible "Back" text ("arrow-left Back"), unlike
            // this file's custom SVG icons (`EraserIcon` etc.), which are all
            // explicitly `aria-hidden`. An explicit `aria-label` here pins the
            // accessible name to exactly "Back" regardless, the same fix
            // Copy Link/Favorite already rely on (there, the icon is the
            // button's ONLY content; here it's paired with visible text).
            aria-label={t("globalMap.quickView.actions.back")}
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            className="!h-6 w-fit self-start !px-1 text-xs text-slate-500"
          >
            {t("globalMap.quickView.actions.back")}
          </Button>
        )}

        <Flex justify="space-between" align="flex-start" gap={8}>
          <Flex align="center" gap={10} className="min-w-0">
            {/* Entity avatar — the same Leads/Projects icon+color pair the
                KPI row and Nearby breakdown already use, so the quick view
                reads as the same visual system rather than a second,
                disconnected color language. */}
            {data && (
              <EntityGlyphTile entityType={data.entity_type} size={44} />
            )}
            {/* Name first, then one muted "<Entity> • <reference>" line with
                the address under it — the approved header hierarchy. Same
                three values as before, only their order and weight changed.
                */}
            <Flex vertical gap={0} className="min-w-0">
              <Title
                level={5}
                className="!mb-0 min-w-0 truncate !text-[19px] font-semibold !text-[#0F172A]"
              >
                {data?.title ?? ""}
              </Title>
              {data && (
                <Text className="truncate text-[13px] text-slate-500">
                  {t(`globalMap.entityLabel.${data.entity_type}`)}
                  {data.reference ? ` \u2022 ${data.reference}` : ""}
                </Text>
              )}
              {data?.location.address && (
                <Text className="truncate text-xs text-slate-400">
                  {data.location.address}
                </Text>
              )}
            </Flex>
          </Flex>
          <Flex gap={2} align="center" className="shrink-0">
            {/* Copy Link reuses the app's existing `?feature=` deep-link
                contract, so the copied URL genuinely reopens this record.
                Favorite is a real, working per-user star (`LeadFavorite` on
                the backend) — permanent until toggled again, never a
                session-only UI flag. "More" (a 3-dot menu with no defined
                content) is removed entirely per explicit product direction,
                alongside the Team tab it would have grown to expose. */}
            <Tooltip title={t("globalMap.quickView.actions.copyLink")}>
              <Button
                type="text"
                size="small"
                icon={<LinkOutlined />}
                aria-label={t("globalMap.quickView.actions.copyLink")}
                onClick={handleCopyLink}
              />
            </Tooltip>
            <Tooltip
              title={
                isFavorite
                  ? t("globalMap.quickView.actions.unfavorite")
                  : t("globalMap.quickView.actions.favorite")
              }
            >
              <Button
                type="text"
                size="small"
                disabled={!data}
                icon={isFavorite ? <StarFilled /> : <StarOutlined />}
                aria-label={
                  isFavorite
                    ? t("globalMap.quickView.actions.unfavorite")
                    : t("globalMap.quickView.actions.favorite")
                }
                aria-pressed={isFavorite}
                onClick={handleToggleFavorite}
                className={isFavorite ? "!text-amber-400" : undefined}
              />
            </Tooltip>
            <Button
              ref={closeButtonRef}
              type="text"
              size="small"
              icon={<CloseOutlined />}
              aria-label={t("globalMap.quickView.actions.close")}
              onClick={handleClose}
            />
          </Flex>
        </Flex>

        {isLoading && (
          <Flex
            justify="center"
            align="center"
            role="status"
            aria-live="polite"
            className="py-6"
          >
            <Spin />
          </Flex>
        )}

        {isError && (
          <Text type="secondary">
            {t(
              parseFeatureId(projectId).entityType === "lead"
                ? "globalMap.quickView.unavailableLead"
                : "globalMap.quickView.unavailableProject"
            )}
          </Text>
        )}

        {data && !isError && config && (
          <>
            {/* Status pill — a soft rounded pill rather than antd `Tag`'s
                boxed look, matching the reference. Color comes from the same
                real, canonical per-status palettes the rest of the app uses
                for these entities (`PROJECT_STATUSES_MAP`/`leadStatusStyles`
                via `globalMapStatusStyle`), not a Global Map-specific guess.
                The reference's second "On Track" pill (an SLA/health
                indicator) is not reproduced — this app has no SLA data on
                this payload (confirmed absent everywhere, not just here). */}
            <Flex gap={8} align="center" wrap>
              <span
                className="rounded-full px-3 py-1 text-xs font-medium"
                style={globalMapStatusStyle(
                  data.entity_type,
                  data.status_label
                )}
              >
                {data.status_label}
              </span>
            </Flex>

            {badge.approximate && <Tag bordered>{badge.text}</Tag>}

            <Tabs
              size="small"
              activeKey={activeTab}
              items={tabItems}
              onChange={setActiveTab}
            />
          </>
        )}
      </div>

      {data && !isError && config && (
        <>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-3">
            {activeTab === "overview" && (
              <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
                {/* A real bar, not a plain-text row — matches the real
                    Project Progress bar's own presentation
                    (`ProjectViewIndicators.tsx`), reading the SAME
                    `progress_percent` figure so the two can never disagree.
                    Project-only: a Lead's payload carries no such field.
                    `null` (no milestones yet) still shows the row — a real
                    0% and an empty bar, per explicit product direction —
                    rather than dropping it entirely, since this component
                    only ever renders from the single-record detail fetch,
                    which always computes this field for a project (unlike
                    the `/list`/`/search` shapes, which don't). */}
                {data.entity_type === "project" && (
                  <React.Fragment>
                    <dt className="text-gray-500">
                      {t("globalMap.quickView.fields.progress")}
                    </dt>
                    <dd className="m-0 min-w-0">
                      <Progress
                        percent={data.progress_percent ?? 0}
                        size="small"
                        strokeColor={getProgressColor(
                          data.progress_percent ?? 0
                        )}
                        // antd's own default swaps the percent text for a
                        // plain check icon once `percent` reaches 100 — an
                        // explicit `format` always wins over that built-in
                        // swap, so the number stays visible at every value,
                        // 100% included.
                        format={(percent) => `${percent}%`}
                      />
                    </dd>
                  </React.Fragment>
                )}
                {fieldRows.map((row) => (
                  <React.Fragment key={row.label}>
                    <dt className="text-gray-500">{row.label}</dt>
                    <dd className="m-0 min-w-0 truncate text-right font-medium">
                      {row.value}
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            )}

            {activeTab === "nearby" && (
              <QuickViewNearby
                status={nearby.status}
                rows={nearby.rows}
                counts={nearby.counts}
                onSelectFeature={onSelectNearbyFeature}
              />
            )}

            {activeTab === "files" && (
              <QuickViewFiles
                entityId={data.id}
                entityType={data.entity_type}
              />
            )}

            {/* Per explicit product direction, this counts-only breakdown
                must stay visible under Overview AND Files — not confined to
                the dedicated Nearby tab above, which keeps its own copy of
                the same breakdown plus the filterable row list this summary
                omits. */}
            {activeTab !== "nearby" && (
              <NearbyBreakdownSummary
                status={nearby.status}
                counts={nearby.counts}
              />
            )}
          </div>

          <Flex
            gap={8}
            wrap
            className="shrink-0 border-t border-gray-100 p-4 pt-3"
          >
            <Button type="primary" onClick={() => onOpenProject(data.id)}>
              {t(config.openLabel)}
            </Button>
            <Button
              // Same fix as the Back button above — antd's built-in
              // `EnvironmentOutlined` renders its own un-hidden
              // `aria-label="environment"`, which would otherwise merge into
              // this button's computed accessible name alongside the visible
              // "View on Map" text.
              aria-label={t("globalMap.quickView.actions.viewOnMap")}
              icon={<EnvironmentOutlined />}
              onClick={() => onRecenter(data.id)}
            >
              {t("globalMap.quickView.actions.viewOnMap")}
            </Button>
          </Flex>
        </>
      )}
    </div>
  )
}

export default EntityQuickView
